import React, { useEffect, useState } from 'react';

// Nexus Core (DESIGN.md §5.1 Rightside Drawer)
// 右からスライドイン (400ms) + フェードイン (200ms)、退場時はその逆
// フェードを先に終わらせ、スライドが残ることで「スライドイン感」を強調
// オーバーレイクリック / ESC キーで閉じる
const FADE_MS = 200;
const SLIDE_MS = 400;
const ANIMATION_MS = SLIDE_MS; // 退場アンマウント待機時間 = 最大値

export default function Drawer({
  isOpen,
  onClose,
  title,
  children,
  width = '640px',
  footer,
}) {
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(false);

  // マウント / アンマウント制御
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // マウント直後はオフ画面、次フレームで visible=true
      let id2;
      const id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(id1);
        if (id2 !== undefined) cancelAnimationFrame(id2);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), ANIMATION_MS);
    return () => clearTimeout(t);
  }, [isOpen]);

  // ESC キー
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-30">
      {/* Overlay (グレー効果なし、ブラー効果あり。タイトルバー/ツールバーは上層 z で素のまま) */}
      <div
        className={`absolute inset-0 bg-transparent backdrop-blur-[2px] ${visible ? 'opacity-100' : 'opacity-0'}`}
        style={{ transition: `opacity ${FADE_MS}ms ease, backdrop-filter ${FADE_MS}ms ease` }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel (スライド + フェードを別時間で) */}
      <aside
        role="dialog"
        aria-modal="true"
        style={{
          width,
          transition: `opacity ${FADE_MS}ms ease, transform ${SLIDE_MS}ms ease`,
        }}
        className={`
          absolute top-[80px] bottom-3 right-0 max-w-[100vw]
          bg-surface-container-lowest border-l border-border-light shadow-2xl rounded-l-xl
          flex flex-col overflow-hidden
          ${visible ? 'translate-x-0 opacity-100' : 'translate-x-16 opacity-0'}
        `}
      >
        {/* Header */}
        {(title || onClose) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
            <div className="text-base font-semibold text-text-primary truncate">{title}</div>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-md text-outline hover:bg-light-neutral hover:text-text-primary transition-colors"
              aria-label="閉じる"
            >
              <span className="material-symbols-outlined text-[20px] leading-none">close</span>
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto">{children}</div>

        {/* Footer (任意) */}
        {footer && <div className="px-6 py-3 border-t border-border-light">{footer}</div>}
      </aside>
    </div>
  );
}
